import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import {
    Alert,
    Box,
    Button,
    ColumnLayout,
    Header,
    Pagination,
    SpaceBetween,
    StatusIndicator,
    Table,
    TextFilter,
} from "@cloudscape-design/components";
import {
    getLeadershipRiskSummary,
    getLeadershipRiskDetails,
    getLeadershipRiskDetailsSize,
    getLeadershipPackageList,
    getLeadershipPackageDependencies,
} from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import {
    GetLeadershipPackageDependenciesResponse,
    GetLeadershipPackageListResponse,
    GetLeadershipRiskDetailsResponse,
    GetLeadershipRiskDetailsSizeResponse,
    GetLeadershipRiskSummaryResponse,
    LeadershipDependencyRisk,
    LeadershipPackageRisk,
    LeadershipRiskSummary,
} from "src/core/sentinel/types";
import { useParams, useNavigate } from "react-router-dom";
import {
    recommendedVersionCell,
    riskStatusCell,
    vendorGuidanceStatusCell,
} from "src/web/components/DependencyTable/cells";
import { compareByRisk, DependencyTableRow } from "src/web/components/DependencyTable/helpers";
import {
    DependencyCoverageInfoPopover,
    RiskLevelsInfoPopover,
} from "src/web/components/DependencyTable/popovers";
import { LeadershipTree } from "./leadership-tree";
import { LEADERSHIP_OTHER_ORGS_MESSAGE, LEADERSHIP_PAGE_TITLE } from "src/web/constants";

// Default view on leadership page is highest level in org
const DEFAULT_MANAGER_ALIAS = "aagiwal";

interface PackageDependenciesState {
    loading: boolean;
    error: string;
    dependencies: LeadershipDependencyRisk[];
}

type PackageTableRow =
    | { kind: "package"; key: string; pkg: LeadershipPackageRisk }
    | { kind: "dependency"; key: string; dep: LeadershipDependencyRisk }
    | { kind: "status"; key: string; status: "loading" | "error" | "empty"; message: string }
    | { kind: "more"; key: string; packageName: string; remaining: number };

const TABLE_ROW_HEIGHT_PX = 48;
const TABLE_CHROME_PX = 340;
const MIN_PAGE_SIZE = 5;

const pageSizeForHeight = (height: number) =>
    Math.max(MIN_PAGE_SIZE, Math.floor((height - TABLE_CHROME_PX) / TABLE_ROW_HEIGHT_PX));

const SIDEBAR_LAYOUT_MIN_WIDTH_PX = 920;

const useElementWidth = (ref: React.RefObject<HTMLElement>) => {
    const [width, setWidth] = useState<number>(() => window.innerWidth);
    useEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setWidth(entry.contentRect.width);
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return width;
};

const useWindowPageSize = () => {
    const [pageSize, setPageSize] = useState(() => pageSizeForHeight(window.innerHeight));
    useEffect(() => {
        let frame = 0;
        const onResize = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setPageSize(pageSizeForHeight(window.innerHeight)));
        };
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("resize", onResize);
        };
    }, []);
    return pageSize;
};

interface LeadershipDashboardPageProps {
    sentinelController: SentinelController;
}

const highRiskCell = (value: number) =>
    value > 0
        ? <span style={{ color: "#d13212", fontWeight: "bold" }}>{value}</span>
        : <span style={{ color: "#5f6b7a" }}>{value}</span>;

const atRiskCell = (value: number) =>
    value > 0
        ? <span style={{ color: "#ff8c00", fontWeight: "bold" }}>{value}</span>
        : <span style={{ color: "#5f6b7a" }}>{value}</span>;

const upgradeCell = (value: number) =>
    value > 0
        ? <span style={{ color: "#0972d3" }}>{value}</span>
        : <span style={{ color: "#5f6b7a" }}>{value}</span>;

// sizeInMB is a Double; show KB below 1 MB so small exports don't read "0.0 MB".
const formatDownloadSize = (sizeInMB?: number) => {
    if (sizeInMB === undefined || sizeInMB === null || Number.isNaN(sizeInMB)) return "";
    // The API reports decimal MB (bytes / 1,000,000), so KB is also decimal.
    if (sizeInMB < 1) return `${Math.max(1, Math.round(sizeInMB * 1000))} KB`;
    return `${sizeInMB.toFixed(1)} MB`;
};

const toDependencyTableRow = (dep: LeadershipDependencyRisk): DependencyTableRow => ({
    dependencyName: dep.dependencyName,
    version: dep.currentVersion,
    vendorGuidanceStatus: dep.currentVersionStatus,
    recommendedVersion: dep.recommendedVersion,
    riskStatus: dep.riskStatus,
});

export const LeadershipDashboardPage: React.FC<LeadershipDashboardPageProps> = (
    props: LeadershipDashboardPageProps,
) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { managerAlias } = useParams<{ managerAlias: string }>();
    const alias = managerAlias || DEFAULT_MANAGER_ALIAS;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [orgNoticeVisible, setOrgNoticeVisible] = useState(true);
    const [manager, setManager] = useState<LeadershipRiskSummary | null>(null);
    const [summary, setSummary] = useState<GetLeadershipRiskSummaryResponse | null>(null);
    const [packages, setPackages] = useState<LeadershipPackageRisk[]>([]);
    const [packagesLoading, setPackagesLoading] = useState(true);
    const [packagesError, setPackagesError] = useState("");
    const [expandedPackages, setExpandedPackages] = useState<Record<string, boolean>>({});
    const [dependenciesByPackage, setDependenciesByPackage] =
        useState<Record<string, PackageDependenciesState>>({});
    const [packageFilteringText, setPackageFilteringText] = useState("");
    const [packagePageIndex, setPackagePageIndex] = useState(1);
    const [downloadUrl, setDownloadUrl] = useState("");
    const [downloadSizeInMB, setDownloadSizeInMB] = useState<number | undefined>(undefined);
    // How many dependencies each expanded package shows; "Show more" raises it.
    const [dependencyLimits, setDependencyLimits] = useState<Record<string, number>>({});
    const pageSize = useWindowPageSize();

    useEffect(() => {
        dispatch(setPageTitle(LEADERSHIP_PAGE_TITLE));
        dispatch(removeInfoMessage({ id: "message_1" }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setSummary(null);
        setDownloadUrl("");
        const fetchSummary = async () => {
            setLoading(true);
            setError("");
            try {
                const summaryResponse: any = await dispatch(
                    getLeadershipRiskSummary({
                        sentinelController: props.sentinelController,
                        managerAlias: alias,
                    }),
                ).unwrap();
                if (cancelled) return;
                const summaryPayload = summaryResponse?.payload as GetLeadershipRiskSummaryResponse;
                setSummary(summaryPayload ?? null);
                setManager(summaryPayload?.manager ?? null);

                const detailsResponse: any = await dispatch(
                    getLeadershipRiskDetails({
                        sentinelController: props.sentinelController,
                        managerAlias: alias,
                    }),
                ).unwrap();
                if (cancelled) return;
                const details = detailsResponse?.payload as GetLeadershipRiskDetailsResponse;
                setDownloadUrl(details?.downloadUrl ?? "");
                setLoading(false);
            } catch (e) {
                if (cancelled) return;
                setLoading(false);
                setError(`Failed to load leadership risk summary for ${alias}.`);
            }
        };
        fetchSummary();
        return () => {
            cancelled = true;
        };
    }, [alias]);

    useEffect(() => {
        let cancelled = false;
        setDownloadSizeInMB(undefined);
        const fetchDownloadSize = async () => {
            try {
                const response: any = await dispatch(
                    getLeadershipRiskDetailsSize({
                        sentinelController: props.sentinelController,
                        managerAlias: alias,
                    }),
                ).unwrap();
                if (cancelled) return;
                const size = response?.payload as GetLeadershipRiskDetailsSizeResponse;
                setDownloadSizeInMB(size?.sizeInMB);
            } catch (e) {
                if (!cancelled) setDownloadSizeInMB(undefined);
            }
        };
        fetchDownloadSize();
        return () => {
            cancelled = true;
        };
    }, [alias]);

    useEffect(() => {
        let cancelled = false;
        setExpandedPackages({});
        setDependencyLimits({});
        setPackageFilteringText("");
        setPackagePageIndex(1);
        const fetchPackages = async () => {
            setPackagesLoading(true);
            setPackagesError("");
            try {
                const response: any = await dispatch(
                    getLeadershipPackageList({
                        sentinelController: props.sentinelController,
                        managerAlias: alias,
                    }),
                ).unwrap();
                if (cancelled) return;
                const list = response?.payload as GetLeadershipPackageListResponse;
                setPackages(list?.packages ?? []);
            } catch (e) {
                if (cancelled) return;
                setPackages([]);
                setPackagesError(`Failed to load packages for ${alias}.`);
            }
            setPackagesLoading(false);
        };
        fetchPackages();
        return () => {
            cancelled = true;
        };
    }, [alias]);

    const fetchDependencies = async (packageName: string) => {
        setDependenciesByPackage((prev) => ({
            ...prev,
            [packageName]: { loading: true, error: "", dependencies: [] },
        }));
        try {
            const response: any = await dispatch(
                getLeadershipPackageDependencies({
                    sentinelController: props.sentinelController,
                    packageName,
                }),
            ).unwrap();
            const result = response?.payload as GetLeadershipPackageDependenciesResponse;
            setDependenciesByPackage((prev) => ({
                ...prev,
                [packageName]: { loading: false, error: "", dependencies: result?.dependencies ?? [] },
            }));
        } catch (e) {
            setDependenciesByPackage((prev) => ({
                ...prev,
                [packageName]: {
                    loading: false,
                    error: `Failed to load dependencies for ${packageName}.`,
                    dependencies: [],
                },
            }));
        }
    };

    const togglePackage = (packageName: string) => {
        const expanding = !expandedPackages[packageName];
        setExpandedPackages((prev) => ({ ...prev, [packageName]: expanding }));

        const cached = dependenciesByPackage[packageName];
        if (expanding && (!cached || cached.error)) {
            fetchDependencies(packageName);
        }
    };

    const goToAlias = (target: string) => {
        if (target && target.trim()) {
            navigate(`/leadership-dashboard/${encodeURIComponent(target.trim())}`);
        }
    };

    const filteredPackages = packages
        .filter((p) =>
            !packageFilteringText ||
            p.packageName.toLowerCase().includes(packageFilteringText.toLowerCase()),
        )
        .sort((a, b) =>
            b.highRiskCount - a.highRiskCount ||
            b.atRiskCount - a.atRiskCount ||
            b.upgradeAvailableCount - a.upgradeAvailableCount ||
            a.packageName.localeCompare(b.packageName),
        );
    const paginatedPackages = filteredPackages.slice(
        (packagePageIndex - 1) * pageSize,
        packagePageIndex * pageSize,
    );
    const totalPackagePages = Math.ceil(filteredPackages.length / pageSize);

    const packageRows: PackageTableRow[] = paginatedPackages.reduce<PackageTableRow[]>((acc, pkg) => {
        const rows: PackageTableRow[] = [{ kind: "package", key: pkg.packageName, pkg }];
        if (!expandedPackages[pkg.packageName]) return acc.concat(rows);
        const state = dependenciesByPackage[pkg.packageName];
        if (!state || state.loading) {
            rows.push({
                kind: "status",
                key: `${pkg.packageName}/loading`,
                status: "loading",
                message: "Loading dependencies",
            });
        } else if (state.error) {
            rows.push({
                kind: "status",
                key: `${pkg.packageName}/error`,
                status: "error",
                message: state.error,
            });
        } else if (state.dependencies.length === 0) {
            rows.push({
                kind: "status",
                key: `${pkg.packageName}/empty`,
                status: "empty",
                message: "No dependencies found",
            });
        } else {
            const limit = dependencyLimits[pkg.packageName] ?? pageSize;
            const sortedDependencies = [...state.dependencies].sort(compareByRisk);
            sortedDependencies.slice(0, limit).forEach((dep) => rows.push({
                kind: "dependency",
                key: `${pkg.packageName}/${dep.dependencyName}`,
                dep,
            }));
            if (state.dependencies.length > limit) {
                rows.push({
                    kind: "more",
                    key: `${pkg.packageName}/more`,
                    packageName: pkg.packageName,
                    remaining: state.dependencies.length - limit,
                });
            }
        }
        return acc.concat(rows);
    }, []);

    const showMoreDependencies = (packageName: string) =>
        setDependencyLimits((prev) => ({
            ...prev,
            [packageName]: (prev[packageName] ?? pageSize) + pageSize,
        }));

    // A taller window shrinks the page count; keep the current page in range.
    useEffect(() => {
        setPackagePageIndex((page) => Math.min(page, Math.max(1, totalPackagePages)));
    }, [totalPackagePages]);

    const showDependencyColumns = packageRows.some((row) => row.kind !== "package");
    const packageTableColumns = [
        "name",
        "highRiskCount",
        "atRiskCount",
        "upgradeAvailableCount",
        "totalDependencies",
        ...(showDependencyColumns
            ? ["currentVersion", "currentVersionStatus", "recommendedVersion", "riskStatus"]
            : []),
    ];

    const packageCell = (row: PackageTableRow, render: (p: LeadershipPackageRisk) => React.ReactNode) =>
        row.kind === "package" ? render(row.pkg) : "";
    const dependencyCell = (row: PackageTableRow, render: (d: LeadershipDependencyRisk) => React.ReactNode) =>
        row.kind === "dependency" ? render(row.dep) : "";

    const nameCell = (row: PackageTableRow) => {
        if (row.kind === "package") {
            const expanded = !!expandedPackages[row.pkg.packageName];
            return (
                <SpaceBetween direction="horizontal" size="xxs">
                    <Button
                        variant="inline-icon"
                        iconName={expanded ? "caret-down-filled" : "caret-right-filled"}
                        ariaLabel={`Dependencies of ${row.pkg.packageName}`}
                        ariaExpanded={expanded}
                        onClick={() => togglePackage(row.pkg.packageName)}
                    />
                    <b>{row.pkg.packageName}</b>
                </SpaceBetween>
            );
        }
        if (row.kind === "dependency") {
            return <Box padding={{ left: "l" }}>{row.dep.dependencyName}</Box>;
        }
        if (row.kind === "more") {
            const next = Math.min(pageSize, row.remaining);
            return (
                <Box padding={{ left: "l" }}>
                    <Button variant="link" onClick={() => showMoreDependencies(row.packageName)}>
                        {`Show ${next} more (${row.remaining} remaining)`}
                    </Button>
                </Box>
            );
        }
        const indicatorType = row.status === "loading" ? "loading" : row.status === "error" ? "error" : "info";
        return (
            <Box padding={{ left: "l" }}>
                <StatusIndicator type={indicatorType}>{row.message}</StatusIndicator>
            </Box>
        );
    };

    const downloadSize = formatDownloadSize(downloadSizeInMB);

    const lastRefreshed = manager?.lastRefreshed
        ? new Date(manager.lastRefreshed).toUTCString()
        : "";

    const layoutRef = useRef<HTMLDivElement>(null);
    const narrowLayout = useElementWidth(layoutRef) < SIDEBAR_LAYOUT_MIN_WIDTH_PX;

    return (
        <SentinelBasePage>
            <SpaceBetween size="l">
                {orgNoticeVisible && (
                    <Alert type="info" dismissible onDismiss={() => setOrgNoticeVisible(false)}>
                        {LEADERSHIP_OTHER_ORGS_MESSAGE}
                    </Alert>
                )}

                {error && (
                    <Alert type="error" dismissible onDismiss={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <div
                    ref={layoutRef}
                    style={
                        narrowLayout
                            ? { display: "flex", flexDirection: "column", gap: 20 }
                            : { display: "flex", alignItems: "flex-start", gap: 20 }
                    }
                >

                    <div
                        style={
                            narrowLayout
                                ? { width: "100%" }
                                : { flex: "0 0 340px", position: "sticky", top: 0 }
                        }
                    >
                        <LeadershipTree
                            sentinelController={props.sentinelController}
                            selectedAlias={alias}
                            selectedSummary={summary}
                            selectedLoading={loading}
                            onSelect={goToAlias}
                        />
                    </div>
                    <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                        <SpaceBetween size="l">
                            {manager && (
                                <SentinelBasePage
                                    header={`${manager.managerName} (${manager.managerAlias})`}
                                    description={
                                        `Level ${manager.level} · org totals, de-duplicated` +
                                        (lastRefreshed ? ` · as of ${lastRefreshed}` : "")
                                    }
                                >
                                    <ColumnLayout columns={5} variant="text-grid">
                                        <div>
                                            <Box variant="awsui-key-label">High risk</Box>
                                            <Box fontSize="display-l">{highRiskCell(manager.highRiskCount)}</Box>
                                        </div>
                                        <div>
                                            <Box variant="awsui-key-label">At risk</Box>
                                            <Box fontSize="display-l">{atRiskCell(manager.atRiskCount)}</Box>
                                        </div>
                                        <div>
                                            <Box variant="awsui-key-label">Upgrade available</Box>
                                            <Box fontSize="display-l">{upgradeCell(manager.upgradeAvailableCount)}</Box>
                                        </div>
                                        <div>
                                            <Box variant="awsui-key-label">Packages</Box>
                                            <Box fontSize="display-l">{manager.totalPackages}</Box>
                                        </div>
                                        <div>
                                            <Box variant="awsui-key-label">Dependencies</Box>
                                            <Box fontSize="display-l">{manager.totalDependencies}</Box>
                                        </div>
                                    </ColumnLayout>
                                </SentinelBasePage>
                            )}

                            {packagesError && (
                                <Alert type="error" dismissible onDismiss={() => setPackagesError("")}>
                                    {packagesError}
                                </Alert>
                            )}

                            <Table
                                items={packageRows}
                                trackBy="key"
                                visibleColumns={packageTableColumns}
                                columnDefinitions={[
                                    {
                                        id: "name",
                                        header: (
                                            <SpaceBetween direction="horizontal" size="xs">
                                                <span>Package / dependency</span>
                                                <DependencyCoverageInfoPopover />
                                            </SpaceBetween>
                                        ),
                                        cell: nameCell,
                                        isRowHeader: true,
                                    },
                                    {
                                        id: "highRiskCount",
                                        header: "High risk",
                                        cell: (e: PackageTableRow) => packageCell(e, (p) => highRiskCell(p.highRiskCount)),
                                    },
                                    {
                                        id: "atRiskCount",
                                        header: "At risk",
                                        cell: (e: PackageTableRow) => packageCell(e, (p) => atRiskCell(p.atRiskCount)),
                                    },
                                    {
                                        id: "upgradeAvailableCount",
                                        header: "Upgrade available",
                                        cell: (e: PackageTableRow) => packageCell(e, (p) => upgradeCell(p.upgradeAvailableCount)),
                                    },
                                    {
                                        id: "totalDependencies",
                                        header: "Dependencies",
                                        cell: (e: PackageTableRow) => packageCell(e, (p) => p.totalDependencies),
                                    },
                                    {
                                        id: "currentVersion",
                                        header: "Current Version",
                                        cell: (e: PackageTableRow) => dependencyCell(e, (d) => d.currentVersion),
                                    },
                                    {
                                        id: "currentVersionStatus",
                                        header: "Current Version Status",
                                        cell: (e: PackageTableRow) => dependencyCell(e, (d) => vendorGuidanceStatusCell(d.currentVersionStatus)),
                                    },
                                    {
                                        id: "recommendedVersion",
                                        header: "Recommended Version",
                                        cell: (e: PackageTableRow) => dependencyCell(e, (d) => recommendedVersionCell(toDependencyTableRow(d))),
                                    },
                                    {
                                        id: "riskStatus",
                                        header: (
                                            <SpaceBetween direction="horizontal" size="xs">
                                                <span>Risk</span>
                                                <RiskLevelsInfoPopover />
                                            </SpaceBetween>
                                        ),
                                        cell: (e: PackageTableRow) => dependencyCell(e, (d) => riskStatusCell(d.riskStatus)),
                                    },
                                ]}
                                loading={packagesLoading}
                                loadingText="Loading packages"
                                empty={
                                    <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                                        <b>No packages</b>
                                    </Box>
                                }
                                filter={
                                    <TextFilter
                                        filteringPlaceholder="Filter packages"
                                        filteringAriaLabel="Filter packages"
                                        filteringText={packageFilteringText}
                                        onChange={({ detail }) => {
                                            setPackageFilteringText(detail.filteringText);
                                            setPackagePageIndex(1);
                                        }}
                                        countText={`${filteredPackages.length} matches`}
                                    />
                                }
                                header={
                                    <Header
                                        counter={`(${packages.length})`}
                                        description="Expand a package to see its dependencies."
                                        actions={
                                            <Button
                                                iconName="download"
                                                variant="primary"
                                                disabled={!downloadUrl}
                                                href={downloadUrl}
                                                target="_blank"
                                                download={`leadership-risk-${alias}.csv`}
                                            >
                                                {downloadSize ? `Download details (CSV, ${downloadSize})` : "Download details (CSV)"}
                                            </Button>
                                        }
                                    >
                                        Packages
                                    </Header>
                                }
                                pagination={
                                    <Pagination
                                        currentPageIndex={packagePageIndex}
                                        pagesCount={totalPackagePages}
                                        onChange={({ detail }) => setPackagePageIndex(detail.currentPageIndex)}
                                    />
                                }
                            />
                        </SpaceBetween>
                    </div>
                </div>
            </SpaceBetween>
        </SentinelBasePage>
    );
};

export default LeadershipDashboardPage;
